import React, { useEffect, useMemo } from 'react';
import { graphql, useStaticQuery } from 'gatsby';

import { useSelector } from 'react-redux';
import store, { actions } from '@store';

import { getBlogPostCategories } from '@src/utils/getBlogPostCategories';
import { parseGraphBlogPostsToArray } from '@src/utils/parsers';
import useActivePage from '@src/hooks/useActivePage';

import { StandardSection } from '@commons/standard-section/StandardSection';
import { BlogPostCard } from '@commons/blog-post-card/BlogPostCard';
import { SearchBar } from '@commons/search-bar/SearchBar';
import { Arrow } from '@commons/category-browser/category-tile/Arrow';
import PaginationControls from '@commons/pagination/PaginationControls';
import NewsletterBanner from '@commons/banner-newsletter/NewsletterBanner';

import {
  filterPostsByCategory,
  filterPostsBySearchTerm,
  getHeader,
  getBlogPosts,
  getCurrentPostsData,
} from './helpers';
import { pageSize, firstPageSize } from './constants';

import * as styles from './previous-blog-posts.module.scss';

export const PreviousBlogPostsSection = ({ location }) => {
  const blogsQuerry = useStaticQuery(graphql`
    query PreviousBlogPosts {
      allMarkdownRemark(
        filter: { frontmatter: { layout: { eq: "blog" } } }
        sort: { fields: frontmatter___date, order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              authors {
                frontmatter {
                  surname
                  name
                }
              }
              title
              path
              date
              thumbnail {
                childImageSharp {
                  gatsbyImageData(layout: FULL_WIDTH)
                }
                extension
                publicURL
              }
              blogKeywords
              searchKeywords
              postCategories {
                frontmatter {
                  postCategoryName
                }
              }
              postTags {
                frontmatter {
                  postTagName
                }
              }
            }
            wordCount {
              words
            }
          }
        }
      }
    }
  `);

  const { changeBlogCategory, resetBlogCategory, changeSearchTerm, resetSearchTerm, changeBlogPosts } = actions;

  const header = getHeader(location.pathname);

  const { activePage, setCurrentPage } = useActivePage(location);

  const categoriesArray = getBlogPostCategories(blogsQuerry);

  const blogPostsData = useMemo(() => {
    return header === 'All articles'
      ? parseGraphBlogPostsToArray(blogsQuerry.allMarkdownRemark)
      : parseGraphBlogPostsToArray(blogsQuerry.allMarkdownRemark).filter((post) => post.postTags.includes(header));
  }, []);

  const currentBlogCategory = useSelector((state) => state.blogCategoryReducer.currentCategory);
  const searchTerm = useSelector((state) => state.searchTermReducer.searchTerm);
  const blogPosts = useSelector((state) => state.blogPostsReducer.blogPosts);

  useEffect(() => {
    store.dispatch(changeBlogPosts(getBlogPosts(currentBlogCategory, searchTerm, blogPostsData)));
  }, []);

  const currentPostsData = useMemo(
    () => getCurrentPostsData(blogPosts, firstPageSize, activePage, pageSize),
    [activePage, currentBlogCategory, blogPosts, searchTerm],
  );

  useEffect(() => {
    if (currentBlogCategory === 'All') {
      store.dispatch(changeBlogPosts(blogPostsData));
    } else {
      const blogPostsByCategory = filterPostsByCategory(blogPostsData, currentBlogCategory);
      store.dispatch(resetSearchTerm());
      store.dispatch(changeBlogPosts(blogPostsByCategory));
    }
  }, [currentBlogCategory]);

  useEffect(() => {
    if (!searchTerm) {
      currentBlogCategory === 'All' && store.dispatch(changeBlogPosts(blogPostsData));
    } else {
      // We have to search by name, author or post keywords
      const filteredPosts = filterPostsBySearchTerm(blogPostsData, searchTerm);
      store.dispatch(resetBlogCategory());
      store.dispatch(changeBlogPosts(filteredPosts));
    }
  }, [searchTerm]);

  const setCurrentCategory = (category) => {
    store.dispatch(changeBlogCategory(category));
    setCurrentPage(1);
  };

  return (
    <StandardSection id="blog_posts_section" classNames={{ section: styles.section }}>
      {header !== 'All articles' && (
        <a href="/blog/" className={styles.goBackLink}>
          <Arrow />
          Back to all articles
        </a>
      )}
      <h2 className={styles.header}>{header}</h2>
      <SearchBar
        searchTerm={searchTerm}
        setSearchTerm={(newSearchTerm) => store.dispatch(changeSearchTerm(newSearchTerm))}
        categories={categoriesArray}
        currentCategory={currentBlogCategory}
        setCurrentCategory={(newCategory) => setCurrentCategory(newCategory)}
      />

      <div className={styles.postsContainer}>
        {activePage === 1 && (
          <div className={styles.newsletterBanner}>
            <NewsletterBanner />
          </div>
        )}
        {currentPostsData.map((post) => (
          <BlogPostCard key={post.path} {...post} />
        ))}
      </div>
      {blogPosts.length > firstPageSize && (
        <PaginationControls
          currentPage={activePage}
          activePage={activePage}
          totalCount={blogPosts.length}
          pageSize={pageSize}
          firstPageSize={firstPageSize}
          onPageChange={(page) => setCurrentPage(page)}
        />
      )}
    </StandardSection>
  );
};
